/**
 * AR alignment settings — the contract shared by the editor (Redux preference +
 * Firestore payload) and, by mirrored constant, the AR viewer.
 *
 * The viewer's frame is **geo-fixed**: gravity + true north (Y up, Z toward geographic north,
 * X east). See docs/superpowers/specs/2026-07-12-ar-geo-fixed-coordinate-frame-design.md.
 *
 * `frameMode` / `northRef` / `alignSource` are **fixed**, not user settings. They stay in the
 * wire format because the viewer reads them and because session docs and published snapshots
 * written before the controls were removed still carry them — but {@link normalizeArSettings}
 * pins them, so a stale `frameMode: 'session'` in someone's saved preferences can't strand them
 * in a mode with no UI left to leave it by.
 */

/** Frame the viewer renders in. Fixed at `geo`; `session` exists only in stored history. */
export type ARFrameMode = 'session' | 'geo';

/**
 * Heading reference. Fixed at `true` — the viewer applies GPS declination to the magnetic
 * compass heading, and falls back to the raw magnetic heading whenever there is no fix to
 * derive declination from, so `magnetic` needs no separate setting.
 */
export type ARNorthRef = 'true' | 'magnetic';

/** Alignment math. Fixed at `yaw` (leans on SLAM gravity); `fullQuat` was a debug branch. */
export type ARAlignSource = 'yaw' | 'fullQuat';

// The old `cadence`/`intervalSec`/`filter` fields were retired when the viewer moved to a
// validated-averaging heading estimator (it owns windowing + smoothing internally, so those
// knobs no longer mean anything). Old session docs may still carry them; the viewer ignores them.

export interface ARSettings {
  frameMode: ARFrameMode;
  northRef: ARNorthRef;
  alignSource: ARAlignSource;
  /**
   * Remove `orbitControl(...)` from the AR-converted sketch. Unlike the alignment fields
   * above (applied live by the viewer), this one changes the converted code, so it only
   * takes effect on the next Run. Independent of frameMode — orbitControl fights the
   * bridge camera in both session and geo modes.
   */
  stripOrbitControl: boolean;
  /**
   * Remove `background(...)` from the AR-converted sketch. Like `stripOrbitControl`, this
   * changes the converted code, so it only takes effect on the next Run. background() paints
   * an opaque fill over the whole canvas, hiding the AR camera feed behind the overlay, so it
   * is removed out of the box to keep the overlay transparent.
   */
  stripBackground: boolean;
  /**
   * Show the sketch's lil-gui control panel in AR (overlaid on the viewer), instead of
   * hiding it. Off by default: the panel is designed for desktop editing, not an AR
   * headset/phone view, so it stays out of the way unless a student opts in.
   */
  showGuiInAr: boolean;
}

/**
 * `stripOrbitControl: true` (orbitControl never works in AR — the bridge owns the camera) and
 * `stripBackground: true` (background() would paint over the camera feed) mean both are removed
 * out of the box.
 */
export const AR_SETTINGS_DEFAULTS: ARSettings = {
  frameMode: 'geo',
  northRef: 'true',
  alignSource: 'yaw',
  stripOrbitControl: true,
  stripBackground: true,
  showGuiInAr: false
};

/** The fields that are no longer user-settable, and the value they are pinned to. */
export const AR_SETTINGS_FIXED = {
  frameMode: 'geo',
  northRef: 'true',
  alignSource: 'yaw'
} as const;

/**
 * Fill in every field of a partial/absent stored `arSettings`, then pin the fixed ones.
 *
 * The pinning is the point: preferences saved while the coordinate-frame control still existed
 * can carry `frameMode: 'session'`, and a plain defaults-then-stored merge would let that value
 * win forever — with no control left to change it back.
 */
export function normalizeArSettings(
  stored?: Partial<ARSettings> | null
): ARSettings {
  return {
    ...AR_SETTINGS_DEFAULTS,
    ...(stored || {}),
    ...AR_SETTINGS_FIXED
  };
}
